import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import {
  Counties,
  FacilityDetail,
  FacilityRate,
} from "models/facilities/facility-details";
import { Observable } from "rxjs/Observable";
import ContentWrapper from "../../models/content-wrapper/content-wrapper";
import Facility from "../../models/facilities/facility";
import FacilityName from "../../models/facilities/facility-name";
import PatientChartTransferFacility from "../../models/facilities/patient-chart-transfer-facility";
import { HospiceTransferDetails } from "../../models/patient-chart/hospice-transfer.model";
import PatientChartFacilities from "../../models/patient-chart/patient-chart-facilities";
import { ConfigurationService } from "../configuration/configuration.service";

@Injectable()
export class FacilitiesService {
  private readonly facilitiesUrl: string;
  private readonly headers;

  constructor(public http: HttpClient, config: ConfigurationService) {
    this.facilitiesUrl = config.getConfig().apiUrl;
    this.headers = new HttpHeaders().set("Content-Type", "application/json");
  }

  getCounty(zipCode: string) {
    return this.http.get(
      `${this.facilitiesUrl}/company-resources/facilities/lookups/zip-code/${zipCode}`
    );
  }

  getCountyByStateAndZipCode(state: string, zipCode: string) {
    return this.http.get(
      `${this.facilitiesUrl}/address/state/${state}/zip/${zipCode}`
    );
  }

  searchFacilitiesByType(
    query: string,
    id: string,
    page: number = 0,
    size: number = 10
  ): Observable<ContentWrapper<Facility>> {
    let param = "lookupId=";
    if (isNaN(parseInt(id))) {
      param = "type=" + id;
    } else {
      param = "lookupId=" + id;
    }

    return this.http
      .get<ContentWrapper<Facility>>(
        `${this.facilitiesUrl}/company-resources/facilities?page=${page}&limit=${size}&q=${query}&${param}`
      ) //remove &type=${type} for testing.
      .map((x) => {
        let wrapper = new ContentWrapper<Facility>();
        wrapper.content = x.content.map((y) => {
          let f = new Facility();
          f.id = y.id;
          f.name = y.name;
          f.facilityType = y.facilityType;
          f.address = y.address;
          return f;
        });
        return wrapper;
      });
  }

  searchFacilities(query: string): Observable<ContentWrapper<FacilityName>> {
    return this.http
      .get<ContentWrapper<FacilityName>>(
        `${this.facilitiesUrl}/company-resources/facilities?q=${query}`
      )
      .map((x) => {
        console.log(x);
        let wrapper = new ContentWrapper<FacilityName>();
        wrapper.content = x.content.map((y) => {
          let f = new FacilityName();
          f.id = y.id;
          f.name = y.name;
          return f;
        });
        return wrapper;
      });
  }

  //Get the Facility Type
  getFacilityTypeLookUpValues(): Observable<object> {
    return this.http.get<object>(`${this.facilitiesUrl}/lookups`);
  }

  getPatientChartFacilities(
    patientChartId: number
  ): Observable<PatientChartFacilities> {
    return this.http
      .get<PatientChartFacilities>(
        `${this.facilitiesUrl}/patient-charts/${patientChartId}/facilities`
      )
      .map((x) => {
        let patientChart = new PatientChartFacilities();
        let mapped = [];

        x.facilities.forEach((facility: any) => {
          let newFacility: Facility;
          if (facility.facilityType.toLocaleLowerCase() === "hospice") {
            newFacility = new PatientChartTransferFacility(
              facility.hospiceTransferDatetime,
              facility.previousHospiceAdmissionDatetime,
              facility.previousHospiceProviderNumber
            );
          } else {
            newFacility = new Facility();
          }

          newFacility.id = facility.id;
          newFacility.facilityType = facility.facilityType;
          newFacility.address = facility.address;
          newFacility.name = facility.name;

          mapped.push(facility);
        });

        patientChart.hasFuneralHome = x.hasFuneralHome;
        patientChart.hasPharmacy = x.hasPharmacy;
        patientChart.hasHospiceTransfer = x.hasHospiceTransfer;
        patientChart.facilities = mapped;
        return patientChart;
      });
  }

  getFacility(facilityId: number): Observable<Facility> {
    return this.http.get<Facility>(
      `${this.facilitiesUrl}/company-resources/facilities/${facilityId}`
    );
  }

  /**
   * save hospice data
   * @param hospiceTransferData
   * @param {number} id
   * @returns {Observable<any>}
   */
  createHospiceTransfer(
    hospiceTransferData: HospiceTransferDetails,
    id: number
  ) {
    let url: string = `${this.facilitiesUrl}/patient-charts/${id}/facilities`;
    return this.http.post(url, JSON.stringify(hospiceTransferData), {
      headers: this.headers,
    });
  }

  saveFacility(id: number, data: any) {
    let url: string = "";
    if (data.id === undefined) {
      url = `${this.facilitiesUrl}/patient-charts/${id}/facilities`;
    } else {
      url = `${this.facilitiesUrl}/patient-charts/${id}/facilities?facilityID=${data.id}`;
    }
    return this.http.post(url, JSON.stringify(data), { headers: this.headers });
  }

  removePatientFacility(patientChartId: number, facilityId: number) {
    let url: string = `${this.facilitiesUrl}/patient-charts/${patientChartId}/facilities/${facilityId}`;
    return this.http.delete(url);
  }

  markAbsent(patientChartId: number, type: string, clear: boolean = false) {
    let url: string = `${this.facilitiesUrl}/patient-charts/${patientChartId}/facilities/`;
    return this.http.patch(url, { facilityType: type, reset: clear });
  }

  getAllFacilityFromOpenSearch(): Observable<FacilityDetail[]> {
    return this.http.get<FacilityDetail[]>(
      `${this.facilitiesUrl}/facility/facilities`
    );
  }

  getFacilityFromOpenSearch(searchDetails: any): Observable<FacilityDetail[]> {
    return this.http.post<FacilityDetail[]>(
      `${this.facilitiesUrl}/facility/search`,
      JSON.stringify(searchDetails),
      { headers: this.headers }
    );
  }

  saveFacilityDetails(data: any) {
    let url: string = `${this.facilitiesUrl}/facility/save`;
    return this.http.post(url, JSON.stringify(data), { headers: this.headers });
  }

  archiveFacilityToggle(data: any) {
    let url = `${this.facilitiesUrl}/facility/active-toggle`;
    return this.http.post(url, JSON.stringify(data), { headers: this.headers });
  }

  getFacilityRates(facilityId: number) {
    let url: string = `${this.facilitiesUrl}/facility/facility/${facilityId}/rates`;
    return this.http.get<FacilityRate[]>(url);
  }

  getFacilityRatesByCareLevel(facilityId: number, careLevel: number) {
    let url: string = `${this.facilitiesUrl}/facility/facility/${facilityId}/rates/${careLevel}`;
    return this.http.get<FacilityRate[]>(url);
  }

  saveFacilityRate(facilityRate: FacilityRate) {
    let url: string = `${this.facilitiesUrl}/facility/rates/save`;
    return this.http.post(url, JSON.stringify(facilityRate), {
      headers: this.headers,
    });
  }

  getFacilityTypes() {
    return this.http.get<any[]>(
      `${this.facilitiesUrl}/facility/facility-types`
    );
  }

  getFacilityById(facilityId: number) {
    let url: string = `${this.facilitiesUrl}/facility/facility/${facilityId}`;
    return this.http.get<FacilityDetail>(url);
  }

  getCounties(state, zipcode) {
    let url: string = `${this.facilitiesUrl}/address/state/${state}/zip/${zipcode}`;
    return this.http.get<Counties[]>(url);
  }

  deleteFacility(facilityId: number) {
    let url: string = `${this.facilitiesUrl}/facility/facility/${facilityId}`;
    return this.http.delete(url);
  }

  getCareLevels() {
    let url: string = `${this.facilitiesUrl}/facility/care-levels`;
    return this.http.get<any[]>(url);
  }

  saveCareLevels(careLevels: any[]) {
    let url: string = `${this.facilitiesUrl}/facility/care-levels`;
    return this.http.post(url, JSON.stringify(careLevels), {
      headers: this.headers,
    });
  }

  deleteCareLevels(careLevelId: number) {
    let url: string = `${this.facilitiesUrl}/facility/care-levels/${careLevelId}`;
    return this.http.delete<any>(url);
  }
}



// WEBPACK FOOTER //
// ./src/providers/facilities-service/facilities.service.ts